import { useQuery } from '@tanstack/react-query';
import { fetchKpis, fetchAgentPerformance, fetchResolutionTrend, fetchIncidentVolume, fetchCostAvoidance, fetchMttrTrend, fetchCapacityInsights, fetchSlaPredictions } from '@/lib/api';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line
} from 'recharts';
import { BarChart3, TrendingUp, Target, Clock, DollarSign, ShieldCheck, Users, Zap, AlertTriangle, Shield } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { StatusDot } from '@/components/shared/StatusDot';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="p-2 rounded-lg bg-[var(--nexus-bg-surface)] border border-[var(--nexus-border)] shadow-lg">
      <p className="font-mono text-[10px] text-[var(--nexus-text-muted)] mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }}></span>
          <span className="text-[10px] text-[var(--nexus-text-secondary)]">{p.name}:</span>
          <span className="font-mono text-[10px] text-[var(--nexus-text-primary)] tabular-nums">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
        </div>
      ))}
    </div>
  );
};

const BigKpi = ({ label, value, suffix = '', icon: Icon, color, subtext }) => (
  <div className="nexus-card relative overflow-hidden p-4">
    <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}22, transparent)` }} />
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-1">{label}</p>
        <p className="font-heading text-3xl font-semibold tabular-nums" style={{ color }}>{value}{suffix}</p>
        {subtext && <p className="text-[10px] text-[var(--nexus-text-muted)] mt-1">{subtext}</p>}
      </div>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={20} style={{ color }} />
      </div>
    </div>
  </div>
);

export default function AnalyticsPage() {
  const { data: kpis } = useQuery({ queryKey: ['kpis'], queryFn: fetchKpis, refetchInterval: 15000 });
  const { data: mttrData } = useQuery({ queryKey: ['mttr-trend'], queryFn: fetchMttrTrend });
  const { data: agentPerf } = useQuery({ queryKey: ['agent-performance'], queryFn: fetchAgentPerformance });
  const { data: resTrend } = useQuery({ queryKey: ['resolution-trend'], queryFn: fetchResolutionTrend });
  const { data: incVolume } = useQuery({ queryKey: ['incident-volume'], queryFn: fetchIncidentVolume });
  const { data: costData } = useQuery({ queryKey: ['cost-avoidance'], queryFn: fetchCostAvoidance });
  const { data: capInsights } = useQuery({ queryKey: ['capacity-insights'], queryFn: fetchCapacityInsights });
  const { data: slaRisk } = useQuery({ queryKey: ['sla-predictions'], queryFn: fetchSlaPredictions, refetchInterval: 30000 });

  const radarData = (agentPerf || []).slice(0, 6).map(a => ({
    agent: a.name, confidence: a.avg_confidence || 0, success: a.success_rate || 0,
    throughput: Math.min(100, (a.tickets_resolved || 0) / 2),
  }));

  return (
    <div data-testid="analytics-page" className="w-full max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Analytics</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Executive dashboards and operational metrics</p>
        </div>
        <Link to="/analytics/query" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0d3d4d] border border-cyan-500/30 text-[var(--nexus-accent)] text-xs font-semibold hover:bg-[#0f4a5c] transition-colors" data-testid="nlp-query-link">
          <Zap size={12} /> NLP Query
        </Link>
      </div>

      {/* Row 1: Big KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4" data-testid="analytics-kpis">
        <BigKpi label="Auto Resolution Rate" value={kpis?.autonomous_resolution_rate?.toFixed(1) || '0'} suffix="%" icon={Target} color="var(--nexus-accent)" subtext="Target: 80%+" />
        <BigKpi label="MTTR (P1)" value={kpis?.mttr_minutes?.toFixed(1) || '0'} suffix=" min" icon={Clock} color="var(--nexus-warning)" subtext="Target: <15 min" />
        <BigKpi label="SLA Compliance" value={kpis?.sla_compliance?.toFixed(1) || '0'} suffix="%" icon={ShieldCheck} color="var(--nexus-primary-bright)" subtext="Target: 99.5%" />
        <BigKpi label="Incidents Prevented" value={costData?.auto_resolved || 0} icon={Shield} color="var(--nexus-success)" subtext="By CAPACITOR" />
        <BigKpi label="Cost Avoidance" value={costData ? `$${(costData.cost_avoidance_total / 1000).toFixed(0)}k` : '$0'} icon={DollarSign} color="var(--nexus-success)" subtext={costData ? `${costData.fte_equivalent} FTE saved` : ''} />
        <BigKpi label="Agent Actions Today" value={kpis?.agent_actions_last_hour || 0} icon={Zap} color="var(--nexus-accent)" subtext="Live counter" />
      </div>

      {/* Row 2: Resolution Trend + Incident Volume */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Resolution Rate Trend (30 days)</h2>
          </div>
          <div className="p-3">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={resTrend || []} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gAuto" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3}/><stop offset="95%" stopColor="#06B6D4" stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#475569' }} axisLine={{ stroke: '#1E2A45' }} interval={6} />
                <YAxis tick={{ fontSize: 10, fill: '#475569' }} axisLine={{ stroke: '#1E2A45' }} domain={[50, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="auto_resolution" name="Auto Resolution %" stroke="#06B6D4" fill="url(#gAuto)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Incident Volume by Category</h2>
          </div>
          <div className="p-3">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={(incVolume || []).slice(-14)} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#475569' }} axisLine={{ stroke: '#1E2A45' }} />
                <YAxis tick={{ fontSize: 10, fill: '#475569' }} axisLine={{ stroke: '#1E2A45' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Infrastructure" stackId="a" fill="#3B82F6" radius={[0,0,0,0]} />
                <Bar dataKey="Application" stackId="a" fill="#06B6D4" />
                <Bar dataKey="Network" stackId="a" fill="#F59E0B" />
                <Bar dataKey="Database" stackId="a" fill="#EF4444" />
                <Bar dataKey="Security" stackId="a" fill="#6366F1" radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Row 3: MTTR Trend + Agent Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">MTTR Trend (P1/P2)</h2>
          </div>
          <div className="p-3">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={mttrData || []} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#475569' }} axisLine={{ stroke: '#1E2A45' }} interval={6} />
                <YAxis tick={{ fontSize: 10, fill: '#475569' }} axisLine={{ stroke: '#1E2A45' }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="p1" name="P1 MTTR (min)" stroke="#EF4444" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="p2" name="P2 MTTR (min)" stroke="#F59E0B" strokeWidth={2} dot={false} />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Agent Performance Comparison</h2>
          </div>
          <div className="p-3">
            <ResponsiveContainer width="100%" height={220}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="rgba(148,163,184,0.12)" />
                <PolarAngleAxis dataKey="agent" tick={{ fontSize: 9, fill: '#94A3B8' }} />
                <PolarRadiusAxis tick={{ fontSize: 8, fill: '#475569' }} domain={[0, 100]} />
                <Radar name="Confidence" dataKey="confidence" stroke="#06B6D4" fill="#06B6D4" fillOpacity={0.15} strokeWidth={1.5} />
                <Radar name="Success" dataKey="success" stroke="#10B981" fill="#10B981" fillOpacity={0.1} strokeWidth={1.5} />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Row 4: Agent Leaderboard + Cost Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Agent Efficiency Leaderboard</h2>
          </div>
          <ScrollArea className="h-[300px]">
            <table className="w-full">
              <thead className="sticky top-0 bg-[var(--nexus-bg-elevated)]">
                <tr className="border-b border-[var(--nexus-border)]">
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-3 py-2 font-medium">#</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-3 py-2 font-medium">Agent</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-right px-3 py-2 font-medium">Resolved</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-right px-3 py-2 font-medium">Confidence</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-right px-3 py-2 font-medium">Success</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-right px-3 py-2 font-medium">Escalation</th>
                </tr>
              </thead>
              <tbody>
                {(agentPerf || []).map((a, i) => (
                  <tr key={a.name} className="border-b border-[var(--nexus-border)] hover:bg-white/[0.02]">
                    <td className="px-3 py-2 text-xs text-[var(--nexus-text-muted)]">{i + 1}</td>
                    <td className="px-3 py-2"><div><span className="font-mono text-xs text-[var(--nexus-accent)]">{a.name}</span><p className="text-[10px] text-[var(--nexus-text-muted)]">{a.description}</p></div></td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-[var(--nexus-text-primary)] tabular-nums">{a.tickets_resolved}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs tabular-nums" style={{ color: a.avg_confidence >= 90 ? 'var(--nexus-success)' : a.avg_confidence >= 80 ? 'var(--nexus-accent)' : 'var(--nexus-warning)' }}>{a.avg_confidence}%</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-[var(--nexus-success)] tabular-nums">{a.success_rate}%</td>
                    <td className="px-3 py-2 text-right font-mono text-xs tabular-nums" style={{ color: a.escalation_rate > 10 ? 'var(--nexus-warning)' : 'var(--nexus-text-muted)' }}>{a.escalation_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </div>

        {/* Cost Avoidance Card */}
        <div className="nexus-card relative overflow-hidden p-4">
          <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-4 flex items-center gap-2"><DollarSign size={14} className="text-[var(--nexus-success)]" /> Cost Avoidance</h3>
          {costData ? (
            <div className="space-y-4">
              <div><p className="text-[10px] text-[var(--nexus-text-muted)] uppercase tracking-wider">Total Saved</p><p className="font-heading text-2xl font-bold text-[var(--nexus-success)] tabular-nums">${costData.cost_avoidance_total?.toLocaleString()}</p></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <p className="text-[10px] text-[var(--nexus-text-muted)]">Auto Resolved</p>
                  <p className="font-mono text-sm text-[var(--nexus-accent)] tabular-nums">{costData.auto_resolved}</p>
                </div>
                <div className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <p className="text-[10px] text-[var(--nexus-text-muted)]">Human Resolved</p>
                  <p className="font-mono text-sm text-[var(--nexus-text-secondary)] tabular-nums">{costData.human_resolved}</p>
                </div>
                <div className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <p className="text-[10px] text-[var(--nexus-text-muted)]">FTE Hours Saved</p>
                  <p className="font-mono text-sm text-[var(--nexus-success)] tabular-nums">{costData.fte_hours_saved?.toFixed(0)}h</p>
                </div>
                <div className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <p className="text-[10px] text-[var(--nexus-text-muted)]">FTE Equivalent</p>
                  <p className="font-mono text-sm text-[var(--nexus-success)] tabular-nums">{costData.fte_equivalent}</p>
                </div>
              </div>
              <div className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                <p className="text-[10px] text-[var(--nexus-text-muted)]">Monthly Savings</p>
                <p className="font-mono text-lg text-[var(--nexus-success)] tabular-nums">${costData.cost_avoidance_monthly?.toLocaleString()}/mo</p>
              </div>
            </div>
          ) : <div className="h-32 rounded-lg bg-[var(--nexus-bg-base)] animate-pulse" />}
        </div>
      </div>

      {/* Row 5: SLA Breach Risk + Capacity Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4">
        {/* SLA Breach Risk Table */}
        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} className="text-[var(--nexus-danger)]" />
              <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">SLA Breach Risk</h2>
            </div>
          </div>
          <ScrollArea className="h-[250px]">
            <div className="p-2 space-y-1">
              {(slaRisk || []).slice(0, 10).map(t => (
                <Link key={t.ticket_id} to={`/incidents/${t.ticket_id}`} className="flex items-center justify-between p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.03]">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded badge-${t.priority?.toLowerCase()}`}>{t.priority}</span>
                    <span className="font-mono text-[10px] text-[var(--nexus-primary-bright)]">{t.ticket_id}</span>
                    <span className="text-[10px] text-[var(--nexus-text-secondary)] truncate">{t.title?.slice(0, 30)}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`font-mono text-[10px] tabular-nums ${t.remaining_hours <= 0 ? 'text-[var(--nexus-danger)]' : t.remaining_hours < 4 ? 'text-[var(--nexus-warning)]' : 'text-[var(--nexus-text-muted)]'}`}>
                      {t.remaining_hours <= 0 ? 'BREACHED' : `${t.remaining_hours?.toFixed(1)}h`}
                    </span>
                    <span className={`font-mono text-[10px] px-1 py-0 rounded ${t.breach_probability_pct >= 70 ? 'bg-red-500/15 text-red-300' : t.breach_probability_pct >= 40 ? 'bg-amber-500/15 text-amber-200' : 'bg-green-500/15 text-green-300'}`}>
                      {t.breach_probability_pct}%
                    </span>
                  </div>
                </Link>
              ))}
              {(!slaRisk || slaRisk.length === 0) && <div className="text-center py-6 text-[var(--nexus-text-muted)] text-xs">No SLA risks detected</div>}
            </div>
          </ScrollArea>
        </div>

        {/* Capacity Insights */}
        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <div className="flex items-center gap-2">
              <TrendingUp size={14} className="text-[var(--nexus-accent)]" />
              <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">CAPACITOR Capacity Insights</h2>
            </div>
          </div>
          <ScrollArea className="h-[250px]">
            <div className="p-2 space-y-1.5">
              {(capInsights || []).map((ci, i) => (
                <div key={i} className="p-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[10px] text-[var(--nexus-text-primary)]">{ci.ci_name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${ci.recommended_size === 'downsize' ? 'bg-green-500/15 text-green-300' : ci.recommended_size === 'upsize' ? 'bg-amber-500/15 text-amber-200' : 'bg-[var(--nexus-bg-elevated)] text-[var(--nexus-text-muted)]'}`}>
                      {ci.recommended_size}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[var(--nexus-text-muted)]">{ci.metric}: {ci.current_utilization}%</span>
                      <div className="w-16 h-1.5 rounded-full bg-[var(--nexus-bg-elevated)] overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${ci.current_utilization}%`, backgroundColor: ci.current_utilization > 80 ? 'var(--nexus-danger)' : ci.current_utilization > 60 ? 'var(--nexus-warning)' : 'var(--nexus-success)' }} />
                      </div>
                    </div>
                    <span className="font-mono text-[10px] text-[var(--nexus-success)]">{ci.projected_monthly_savings}</span>
                  </div>
                </div>
              ))}
              {(!capInsights || capInsights.length === 0) && <div className="text-center py-6 text-[var(--nexus-text-muted)] text-xs">No capacity insights available</div>}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
